<script setup lang="ts">
import { usePropertyStore } from '~/store/property'
import { Swiper, SwiperSlide } from 'swiper/vue'
import { Thumbs } from 'swiper/modules'
import type { Swiper as SwiperClass } from 'swiper/types'
import { imageUrlWithProxy } from '~/utils/imageproxy'
import 'swiper/css'
import 'swiper/css/thumbs'

const property = usePropertyStore().property
const props = defineProps({
  photos: {
    type: Array,
    required: true,
  },
})

const { photos } = props

const thumbsSwiper = ref<SwiperClass | null>(null)
const swiperModules = [Thumbs]
const lightboxVisible = ref(false)
const lightboxIndex = ref(0)

function onThumbsReady(swiper: SwiperClass) {
  thumbsSwiper.value = swiper
}

function openLightbox(index: number) {
  lightboxIndex.value = index
  lightboxVisible.value = true
}
</script>

<template>
  <section class="room-gallery" v-if="photos && photos.length">
    <Swiper
      class="room-gallery-main"
      :modules="swiperModules"
      :slides-per-view="1"
      :space-between="0"
      :thumbs="{ swiper: thumbsSwiper }"
    >
      <SwiperSlide v-for="(photo, index) in photos" :key="index">
        <img
          class="room-gallery_main-img"
          loading="lazy"
          :src="imageUrlWithProxy((photo as any).url, 658, 344)"
          :alt="(photo as any).description || property.name"
          width="658"
          height="344"
          @click="openLightbox(index)"
        >
      </SwiperSlide>
    </Swiper>
    <Swiper
      class="room-gallery-navigation desktop-only"
      :modules="[]"
      :slides-per-view="'auto'"
      :space-between="8"
      :watch-slides-progress="true"
      :free-mode="true"
      @swiper="onThumbsReady"
    >
      <SwiperSlide v-for="(photo, index) in photos" :key="index">
        <img
          class="room-gallery-navigation_item"
          :src="imageUrlWithProxy((photo as any).url, 72, 72)"
          width="72"
          height="72"
          :alt="(photo as any).description || property.name"
        >
      </SwiperSlide>
    </Swiper>

    <ImageLightbox
      v-model:visible="lightboxVisible"
      :images="(photos as any)"
      :start-index="lightboxIndex"
    />
  </section>
  <section class="room-gallery room-gallery--empty" v-else>
    <img src="/images/image-placeholder.png" class="image-placeholder" alt="" aria-hidden="true">
  </section>
</template>

<style lang="scss" scoped>
$borderRadius: 6px;

.room-gallery {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;

  &--empty {
    min-height: 344px;
    width: 100%;
    background-color: #e2eeff;
    -webkit-border-radius: 8px;
    -moz-border-radius: 8px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;

    img {
      filter: brightness(0) saturate(100%) invert(33%) sepia(1%) saturate(3274%) hue-rotate(314deg) brightness(97%) contrast(85%);
      object-fit: scale-down;
    }
  }

  &_main-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    max-height: 344px;
    border-radius: $borderRadius;
    cursor: pointer;

    @media (max-width: 768px) {
      height: 344px;
    }
  }
}

.room-gallery-main {
  width: 100%;
  border-radius: $borderRadius;
  overflow: hidden;
}

.room-gallery-navigation {
  min-height: 79px;

  :deep(.swiper-slide) {
    width: 73px;
    height: 73px;
    flex-shrink: 0;
    cursor: pointer;
  }

  &_item {
    width: 73px;
    height: 73px;
    border-radius: $borderRadius;
    border: 3px solid var(--blue);
    box-sizing: content-box;
    object-fit: cover;
  }

  :deep(.swiper-slide:not(.swiper-slide-thumb-active) .room-gallery-navigation_item) {
    opacity: 0.5;
    border: none;
  }
}
</style>
